import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, ChevronDown, Globe, Volume2, Camera, Video, Headphones, Monitor, LogIn } from "lucide-react";
import { LanguageSwitcher, useI18n } from "../contexts/I18nContext";
import PromotionalPopup from "./PromotionalPopup";
import DynamicAdSidebar from "./DynamicAdSidebar";

export default function PublicLayout({ children }) {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const { t } = useI18n();

  useEffect(() => { setMobileOpen(false); setServicesOpen(false); }, [location.pathname]);

  const services = [
    { icon: Globe, label: "Sites web & Applications", path: "/devis?service=web" },
    { icon: Monitor, label: "SEO & Marketing digital", path: "/devis?service=seo" },
    { icon: Volume2, label: "Sonorisation evenementielle", path: "/devis?service=sonorisation" },
    { icon: Camera, label: "Reportage photo & video", path: "/devis?service=reportage" },
    { icon: Video, label: "Regie audiovisuelle", path: "/devis?service=regie" },
    { icon: Headphones, label: "Captation audio", path: "/devis?service=captation" },
  ];

  const isServicesActive = ["/plateforme", "/fonctionnalites", "/devis"].includes(location.pathname) || location.pathname.startsWith("/secteurs");

  return (
    <div className="min-h-screen bg-[#0B0F19] text-white">
      {/* Header */}
      <header className="border-b border-[#2A3441] bg-[#0B0F19]/90 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="header-logo">
            <img src="/logo-glotech-icon.png" alt="GLOTECH" className="h-9 w-9 rounded-full object-cover" />
          </Link>

          {/* Desktop Nav — 4 items only */}
          <nav className="hidden lg:flex items-center gap-1 text-sm" data-testid="desktop-nav">
            <Link to="/" className={`px-3 py-2 rounded-lg transition-colors ${location.pathname === "/" ? "text-[#0099CC]" : "text-[#94A3B8] hover:text-white"}`} data-testid="nav-accueil">{t("nav.home")}</Link>

            {/* Services dropdown */}
            <div className="relative" onMouseEnter={() => setServicesOpen(true)} onMouseLeave={() => setServicesOpen(false)}>
              <button className={`px-3 py-2 rounded-lg transition-colors flex items-center gap-1 ${isServicesActive ? "text-[#0099CC]" : "text-[#94A3B8] hover:text-white"}`} data-testid="nav-services">
                {t("nav.services")} <ChevronDown className="h-3 w-3" />
              </button>
              {servicesOpen && (
                <div className="absolute top-full left-0 mt-1 w-64 bg-[#151B2B] border border-[#2A3441] rounded-xl shadow-2xl py-2 z-50">
                  {services.map(s => (
                    <Link key={s.label} to={s.path} className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#94A3B8] hover:text-white hover:bg-[#0099CC]/10 transition-colors">
                      <s.icon className="h-4 w-4 text-[#0099CC] shrink-0" />
                      {s.label}
                    </Link>
                  ))}
                  <div className="border-t border-[#2A3441] mt-1 pt-1">
                    <Link to="/tarifs" className="block px-4 py-2.5 text-sm text-[#0099CC] hover:text-white hover:bg-[#0099CC]/10 transition-colors font-medium">Voir tous les tarifs</Link>
                  </div>
                </div>
              )}
            </div>

            <Link to="/event" className={`px-3 py-2 rounded-lg transition-colors font-semibold ${location.pathname === "/event" ? "text-[#F59E0B]" : "text-[#F59E0B]/80 hover:text-[#F59E0B]"}`} data-testid="nav-evenementiel">{t("nav.events")}</Link>
            <Link to="/evenements-medias" className={`px-3 py-2 rounded-lg transition-colors ${location.pathname === "/evenements-medias" ? "text-[#EF4444]" : "text-[#94A3B8] hover:text-white"}`} data-testid="nav-medias">Medias</Link>
          </nav>

          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link to="/login" data-testid="nav-login-btn" className="hidden sm:inline-flex items-center gap-2 bg-[#0099CC] hover:bg-[#0088B5] text-white px-5 py-2 rounded-lg text-sm font-semibold transition-colors">
              <LogIn className="h-4 w-4" />
              {t("nav.login")}
            </Link>
            <button className="lg:hidden text-[#94A3B8] hover:text-white" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
              {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-[#2A3441] bg-[#0B0F19] px-6 py-4 space-y-1" data-testid="mobile-nav">
            <Link to="/" className="block py-2.5 text-[#94A3B8] hover:text-white font-medium">{t("nav.home")}</Link>
            <div className="py-2.5 text-[#64748b] text-xs uppercase tracking-wider">{t("nav.services")}</div>
            {services.map(s => (
              <Link key={s.label} to={s.path} className="flex items-center gap-2.5 py-2 pl-3 text-[#94A3B8] hover:text-white text-sm">
                <s.icon className="h-3.5 w-3.5 text-[#0099CC]" />
                {s.label}
              </Link>
            ))}
            <Link to="/tarifs" className="block py-2 pl-3 text-[#0099CC] hover:text-white text-sm font-medium">Voir tous les tarifs</Link>
            <Link to="/event" className="block py-2.5 text-[#F59E0B] hover:text-[#F59E0B]/80 font-semibold">{t("nav.events")}</Link>
            <Link to="/evenements-medias" className="block py-2.5 text-[#94A3B8] hover:text-white font-medium">Evenements & Medias</Link>
            <div className="pt-3 flex gap-3">
              <Link to="/login" className="flex-1 flex items-center justify-center gap-2 bg-[#0099CC] text-white py-2.5 rounded-lg text-sm font-semibold" data-testid="mobile-login-btn">
                <LogIn className="h-4 w-4" /> {t("nav.login")}
              </Link>
            </div>
          </div>
        )}
      </header>

      <div className="flex">
        <main className="flex-1 min-w-0">{children}</main>
        <DynamicAdSidebar />
      </div>

      <PromotionalPopup />

      {/* Footer */}
      <footer className="border-t border-[#2A3441] bg-[#080C14]">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid md:grid-cols-4 gap-10 mb-12">
            <div>
              <div className="flex items-center gap-2.5 mb-4">
                <img src="/logo-glotech-full.png" alt="GLOTECH DIGITAL" className="h-10 object-contain" />
              </div>
              <p className="text-[#64748b] text-sm leading-relaxed">Plateforme digitale tout-en-un pour gerer et developper votre activite.</p>
              {/* Social Icons */}
              <div className="flex items-center gap-3 mt-4" data-testid="footer-social">
                <a href="https://www.youtube.com/@GlotechDigital" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-[#EF4444]/10 flex items-center justify-center text-[#EF4444] hover:bg-[#EF4444]/20 transition-all" data-testid="footer-youtube">
                  <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                </a>
                <a href="https://www.tiktok.com/@glotechdigital" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center text-white hover:bg-white/10 transition-all" data-testid="footer-tiktok">
                  <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.11V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.75a8.18 8.18 0 004.76 1.52V6.84a4.84 4.84 0 01-1-.15z"/></svg>
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-white font-semibold text-sm mb-4">Services</h4>
              <div className="space-y-2.5">
                <Link to="/plateforme" className="block text-sm text-[#64748b] hover:text-[#0099CC]">Plateforme</Link>
                <Link to="/fonctionnalites" className="block text-sm text-[#64748b] hover:text-[#0099CC]">Fonctionnalites</Link>
                <Link to="/tarifs" className="block text-sm text-[#64748b] hover:text-[#0099CC]">Tarifs</Link>
                <Link to="/devis" className="block text-sm text-[#64748b] hover:text-[#0099CC]">Demander un devis</Link>
                <Link to="/audit" className="block text-sm text-[#64748b] hover:text-[#0099CC]">Audit SEO gratuit</Link>
                <Link to="/evenements" className="block text-sm text-[#F59E0B]/80 hover:text-[#F59E0B]">Evenementiel</Link>
                <Link to="/evenements-medias" className="block text-sm text-[#EF4444]/80 hover:text-[#EF4444]" data-testid="footer-events-medias">Evenements & Medias</Link>
              </div>
            </div>
            <div>
              <h4 className="text-white font-semibold text-sm mb-4">Ressources</h4>
              <div className="space-y-2.5">
                <Link to="/blog" className="block text-sm text-[#64748b] hover:text-[#0099CC]">Blog</Link>
                <Link to="/realisations" className="block text-sm text-[#64748b] hover:text-[#0099CC]">Realisations</Link>
                <Link to="/secteurs/agences-immobilieres" className="block text-sm text-[#64748b] hover:text-[#0099CC]">Agences immobilieres</Link>
                <Link to="/secteurs/e-commerce" className="block text-sm text-[#64748b] hover:text-[#0099CC]">E-commerce</Link>
                <Link to="/secteurs/consultants" className="block text-sm text-[#64748b] hover:text-[#0099CC]">Consultants & Services</Link>
              </div>
            </div>
            <div>
              <h4 className="text-white font-semibold text-sm mb-4">Contact</h4>
              <div className="space-y-2.5 text-sm text-[#64748b]">
                <a href="mailto:contact@glotechdigital.fr" className="block hover:text-[#0099CC]">contact@glotechdigital.fr</a>
                <a href="tel:+33601150441" className="block hover:text-[#0099CC]">06 01 15 04 41</a>
                <Link to="/contact" className="block hover:text-[#0099CC]">Formulaire de contact</Link>
                <Link to="/login" className="block text-[#0099CC] hover:text-white font-medium">Se connecter</Link>
              </div>
            </div>
          </div>
          <div className="border-t border-[#2A3441] pt-8 flex flex-col md:flex-row gap-4 items-center justify-between text-xs text-[#64748b]">
            <p>&copy; {new Date().getFullYear()} GLOTECH DIGITAL &mdash; {t("footer.rights")}</p>
            <div className="flex gap-6">
              <Link to="/mentions-legales" className="hover:text-white">{t("footer.legal")}</Link>
              <Link to="/politique-confidentialite" className="hover:text-white">{t("footer.privacy")}</Link>
              <Link to="/politique-cookies" className="hover:text-white">{t("footer.cookies")}</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
